package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** One thing waiting on the person asking. */
@Serializable
data class WorkItemDto(
    val projectSlug: String,
    val projectName: String,
    val locale: String,
    /** How many strings this row stands for. */
    val count: Int,
    /**
     * Where clicking goes, with the filter already applied.
     *
     * Built server-side so a row cannot link somewhere that does not match the number beside it —
     * a count of 12 next to a link showing 40 is worse than no link.
     */
    val href: String,
)

/**
 * A section of the queue, or the reason it is missing.
 *
 * [error] rather than an absent section, because a workspace whose database is briefly slow should
 * lose one heading and not the page. The brief is explicit that a slow project degrades to a
 * per-section error rather than blocking everything.
 */
@Serializable
data class WorkSectionDto(
    val id: String,
    val title: String,
    val items: List<WorkItemDto> = emptyList(),
    val error: String? = null,
) {
    val total: Int get() = items.sumOf { it.count }
}

/**
 * Everything waiting on one person, in one call.
 *
 * **The only queue.** Five destinations for one question is a bug: `/app/work`, `/app/my-work`,
 * `/app/inbox`, `/app/review` and a proposal queue all answer "what should I do now", and a
 * translator should not have to learn which one is authoritative.
 *
 * One call, not one per project. `/app` fans out an activity request per project today; repeating
 * that here means a twelve-project workspace makes thirteen requests before a translator sees a
 * string.
 *
 * **Nothing about seats, invoices, credits or API keys appears here for anyone, including owners.**
 * This is the page about the work.
 */
@Serializable
data class WorkResponse(
    /** One honest sentence: "142 strings across 3 projects". Never a number nobody can act on. */
    val summary: String,
    val sections: List<WorkSectionDto> = emptyList(),
    /**
     * Projects the caller can work in, for the empty state.
     *
     * Present even when there is nothing waiting, so an empty queue is still a launcher rather than
     * a dead end.
     */
    val projects: List<WorkItemDto> = emptyList(),
    /**
     * Who invited the caller, when they have done nothing yet.
     *
     * "Nothing assigned yet. Ask Maya which language to start on." is a different and better sentence
     * than "nothing is waiting on you" for somebody on their first day, and naming a person is what
     * makes it actionable.
     */
    val invitedByName: String? = null,
)

/**
 * One unresolved comment naming you.
 *
 * The full list lives at `/app/inbox`, which is a "see all" target rather than a competing queue —
 * two pages answering "what should I do now" is the thing the single queue exists to prevent.
 */
@Serializable
data class MentionDto(
    val projectSlug: String,
    val projectName: String,
    val keyName: String,
    val locale: String? = null,
    /** Somebody else's words. Rendered as text, never as markup. */
    val body: String,
    val author: String? = null,
    val at: String,
    val href: String,
)
